import React, { ForwardedRef, forwardRef } from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { LinkProps as NextLinkProps } from 'next/link'
import MuiLink, { LinkProps as MuiLinkProps } from '@mui/material/Link'
import { styled } from '@mui/material/styles'
import NextLinkComposed, { NextLinkComposedProps } from './NextLinkComposed'

// Add support for the sx prop for consistency with the other branches.
const Anchor = styled('a')({})

export type StyledLinkProps = {
  activeClassName?: string
  as?: NextLinkProps['as']
  href: NextLinkProps['href']
  linkAs?: NextLinkProps['as'] // Useful when the as prop is shallow by styled().
  noLinkStyle?: boolean
} & Omit<NextLinkComposedProps, 'to' | 'linkAs' | 'href'> &
  Omit<MuiLinkProps, 'href'>;

/**
 * A composition of the MUI Link component and the Next.js Link
 * This code was copied and adapted from the Next.js MUI v5 example
 */
const StyledLink = (props: StyledLinkProps, ref: ForwardedRef<HTMLAnchorElement>) => {
  const {
    activeClassName = 'active',
    as,
    className: classNameProps,
    href,
    legacyBehavior,
    linkAs: linkAsProp,
    locale,
    noLinkStyle,
    prefetch,
    replace,
    role, // Link don't have roles.
    scroll,
    shallow,
    ...other
  } = props

  const router = useRouter()
  const pathname = typeof href === 'string' ? href : href?.pathname
  const className = clsx(classNameProps, {
    [activeClassName]: router?.pathname && router?.pathname === pathname && activeClassName,
  })

  const isExternal =
    typeof href === 'string' && (href.indexOf('http') === 0 || href.indexOf('mailto:') === 0)

  if (isExternal) {
    if (noLinkStyle) {
      return <Anchor className={className} href={href} ref={ref} {...other} />
    }

    return <MuiLink className={className} href={href} ref={ref} {...other} />
  }

  const linkAs = linkAsProp || as
  const nextjsProps = {
    to: href,
    linkAs,
    replace,
    scroll,
    shallow,
    prefetch,
    legacyBehavior,
    locale,
  }

  if (noLinkStyle) {
    return <NextLinkComposed className={className} ref={ref} {...nextjsProps} {...other} />
  }

  return (
    <MuiLink
      component={NextLinkComposed}
      className={className}
      ref={ref}
      {...nextjsProps}
      {...other}
    />
  )
}

export default forwardRef<HTMLAnchorElement, StyledLinkProps>(StyledLink)
